import React, { useCallback, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Heading from '@amzn/meridian/heading';
import { CustomDatePicker } from '@components/controls/CustomDatePicker';
import usePublishZone from 'src/store/hooks/usePublishResolver';
import StoreSelector from './StoreSelector';
import GalleryView from './GalleryView';
import SequenceDrawingView from './SequenceDrawingView';
import useMultiStoreState, { PublishZoneResult } from './useMultiStoreState';
import { MultiStoreCopyProps, WorkflowPhase, StoreLoadedData } from './types';

const MultiStoreCopy: React.FC<MultiStoreCopyProps> = ({
  sourceAllocations,
  sourceStoreId,
  selectedDepartment,
  date,
  macroplanogramName,
  onExit,
}) => {
  const [phase, setPhase] = useState<WorkflowPhase>({ type: 'selection' });
  const { mutateAsync: publishZoneFn } = usePublishZone();

  // New live date for publishing — defaults to tomorrow
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultDate = tomorrow.toISOString().split('T')[0];
  const [newLiveDate, setNewLiveDate] = useState(defaultDate);
  const publishTimestamp = new Date(newLiveDate).getTime();
  const {
    stores,
    batchPublishState,
    updateStoreData,
    initializeStores,
    clearState,
    executeBatchPublish,
    retryFailed,
    removeStore,
  } = useMultiStoreState();

  const timestamp = new Date(date).getTime();

  const handleConfirmStores = useCallback(
    (storeIds: string[]) => {
      initializeStores(storeIds);
      setPhase({ type: 'gallery' });
    },
    [initializeStores],
  );

  const handleCancel = useCallback(() => {
    clearState();
    onExit();
  }, [clearState, onExit]);

  const handleStoreClick = useCallback(
    (storeId: string) => {
      const store = stores.find((s) => s.storeId === storeId);
      if (!store) return;
      if (store.readinessStatus === 'needs-sequence') {
        setPhase({ type: 'sequence-drawing', storeId });
      }
      // For ready stores, could expand to detail view — for now just a no-op
    },
    [stores],
  );

  const handleApplyAll = useCallback(async () => {
    const wrappedPublish = async (variables: Parameters<typeof publishZoneFn>[0]) => {
      const result = await publishZoneFn(variables);
      return result as unknown as PublishZoneResult;
    };
    await executeBatchPublish(
      sourceAllocations,
      wrappedPublish,
      macroplanogramName,
      publishTimestamp,
      selectedDepartment,
    );
  }, [executeBatchPublish, sourceAllocations, publishZoneFn, macroplanogramName, publishTimestamp, selectedDepartment]);

  const handleRetryFailed = useCallback(async () => {
    const wrappedPublish = async (variables: Parameters<typeof publishZoneFn>[0]) => {
      const result = await publishZoneFn(variables);
      return result as unknown as PublishZoneResult;
    };
    await retryFailed(sourceAllocations, wrappedPublish, macroplanogramName, publishTimestamp, selectedDepartment);
  }, [retryFailed, sourceAllocations, publishZoneFn, macroplanogramName, publishTimestamp, selectedDepartment]);

  const handleDataLoaded = useCallback(
    (storeId: string, data: StoreLoadedData) => {
      updateStoreData(storeId, {
        floorplanData: data.floorplanData,
        fixtureOrderingMap: data.fixtureOrderingMap,
        readinessStatus: data.readinessStatus,
      });
    },
    [updateStoreData],
  );

  const handleSequenceSaveComplete = useCallback(
    (storeId: string, orderingMap: Map<string, number>) => {
      updateStoreData(storeId, {
        fixtureOrderingMap: orderingMap,
        readinessStatus: 'ready',
      });
      setPhase({ type: 'gallery' });
    },
    [updateStoreData],
  );

  const handleBackToGallery = useCallback(() => {
    setPhase({ type: 'gallery' });
  }, []);

  const handleBackToModeSelection = useCallback(() => {
    clearState();
    onExit();
  }, [clearState, onExit]);

  // Check if source allocations exist — must be after all hooks
  if (sourceAllocations.length === 0) {
    return (
      <Column spacing="400" spacingInset="400">
        <Alert type="error">
          Allocations are required before copying to other stores. Please create allocations on the source store first.
        </Alert>
        <Row>
          <Button type="secondary" onClick={onExit}>
            Back
          </Button>
        </Row>
      </Column>
    );
  }

  return (
    <Column height="100%" width="100%">
      {phase.type === 'selection' && (
        <>
          <Row spacingInset="400" alignmentHorizontal="justify" alignmentVertical="center">
            <Heading level={4}>Copy to Multiple Stores</Heading>
            <Button type="secondary" onClick={handleCancel}>
              Cancel
            </Button>
          </Row>
          <StoreSelector onConfirm={handleConfirmStores} onCancel={handleCancel} sourceStoreId={sourceStoreId} />
        </>
      )}

      {phase.type === 'gallery' && (
        <>
          <Row spacingInset="400 400 0 400" alignmentVertical="bottom" spacing="400">
            <Button type="tertiary" onClick={handleBackToModeSelection}>
              ← Back to Mode Selection
            </Button>
            <CustomDatePicker
              value={newLiveDate}
              onChange={setNewLiveDate}
              label="New Live Date"
              error={!newLiveDate}
            />
          </Row>
          <GalleryView
            targetStores={stores}
            sourceAllocations={sourceAllocations}
            sourceStoreId={sourceStoreId}
            date={timestamp}
            department={selectedDepartment}
            onStoreClick={handleStoreClick}
            onRemoveStore={removeStore}
            onApplyAll={handleApplyAll}
            onRetryFailed={handleRetryFailed}
            onDataLoaded={handleDataLoaded}
            batchPublishState={batchPublishState}
          />
        </>
      )}

      {phase.type === 'sequence-drawing' && (
        <SequenceDrawingView
          storeId={phase.storeId}
          date={timestamp}
          department={selectedDepartment}
          floorplanData={stores.find((s) => s.storeId === phase.storeId)?.floorplanData ?? []}
          onSaveComplete={handleSequenceSaveComplete}
          onBack={handleBackToGallery}
        />
      )}
    </Column>
  );
};

export default MultiStoreCopy;
